<template>
    <ModeSwitcher
        ref="modeSwitcher"
        :main-mode-text="allowCreate ? $t('create') : null"
        :create-mode-text="$t('create_venue')"
        :edit-mode-text="$t('edit_venue')"
        @reset="resetData"
    >
        <template v-slot:additional-mode>
            <VenueDetailsModal
                :venue="selectedLocationPoint"
                @go-to-table="goToTable"
                @load-locations="emit('load-locations')"
            />
        </template>
        <template v-slot:main-mode>
            <slot name="filters" />
            <v-data-table
                :headers="headers"
                :items="items"
                :items-length="items.length"
                :items-per-page="15"
                :items-per-page-options="[15, 25, 50, -1]"
                :loading="isLoading"
                color="primary"
                density="compact"
                :data-cy="`${props.locationType}-table`"
                class="elevation-1 table-server"
                must-sort
                @update:sort-by="sort"
            >
                <template v-slot:item.name="{item}">
                    <span class="text-link" @click="editLocation(item)">
                        {{ getLocaleName(item.name) || item.id }}
                    </span>
                </template>
                <template v-slot:item.street="{item}">
                    {{ getLocaleName(item.street) }}
                </template>
                <template v-slot:item.types="{item}">
                    {{ getTypesText(item.types) }}
                </template>
                <template v-slot:item.coordinates="{item}">
                    <span v-if="item.latitude">{{ item.latitude }}</span>
                    <span v-if="item.longitude">
                        {{ `${item.latitude ? ', ' : ''}${item.longitude}` }}
                    </span>
                </template>
                <template v-slot:item.active="{item}">
                    {{ item.active ? t('active') : t('inactive') }}
                </template>
            </v-data-table>
        </template>
    </ModeSwitcher>
</template>

<script setup lang="ts">
    import type {VenueType} from '~/utils/types/account/locations/types'
    import {useLocalized} from '#imports'
    import {initLocationPoint, initVenue} from '~/utils/types/account/locations/helpers'
    import VenueDetailsModal from '~/components/account/locations/VenueDetailsModal.vue'

    const props = defineProps<{
        items: any[]
        isLoading: boolean
        locationType: string
        allowEdit?: boolean
        allowCreate?: boolean
    }>()
    const emit = defineEmits<{
        (e: 'create'): void
        (e: 'edit', value: VenueType): void
        (e: 'load-locations'): void
    }>()

    const app = useNuxtApp()
    const {t} = useI18n()

    const modeSwitcher = ref(null)
    const selectedLocationPoint = ref<VenueType>(initVenue())

    const headers: ComputedRef<{title: string; key: string}[]> = computed(() => {
        const headers: {title: string; key: string}[] = [
            {title: t('name'), key: 'name'},
            {title: t('country'), key: 'countryName'},
            {title: t('city'), key: 'cityName'},
            {title: t('postal_code'), key: 'postalCode'},
            {title: t('address'), key: 'street'},
            {title: t('available_for'), key: 'types'},
            {title: t('coordinates'), key: 'coordinates', sortable: false},
            {title: t('status'), key: 'active', sortable: false},
        ]

        return headers
    })

    const appLocaleCode: ComputedRef<string> = computed(() => app.$i18n.locale.value)

    function getTypesText(types): string {
        if (!types?.length) return ''

        return types.map(type => t(type.toLowerCase())).join(', ')
    }

    function getLocaleName(nameObj: any) {
        if (!nameObj || (nameObj && !Object.keys(nameObj).length)) return null

        if (nameObj[appLocaleCode.value]) return nameObj[appLocaleCode.value]
        else if (nameObj['en']) return nameObj['en']

        return nameObj[Object.keys(nameObj)[0]]
    }

    const localized = useLocalized()

    function sort(sortItems) {
        for (const {key, order} of sortItems) {
            items.value.sort((a, b) => {
                if (typeof a[key] === 'object') {
                    a = localized(a[key])
                    b = localized(b[key])
                } else {
                    a = a[key] || ''
                    b = b[key] || ''
                }
                if (a === '') return 1
                if (b === '') return -1
                if (order === 'asc') {
                    return a.localeCompare(b)
                } else if (order === 'desc') {
                    return -a.localeCompare(b)
                }
                return 0
            })
        }
    }

    function resetData() {
        selectedLocationPoint.value = initLocationPoint()
    }

    function editLocation(location) {
        selectedLocationPoint.value = location
        modeSwitcher.value.edit()
    }

    function goToTable() {
        resetData()
        modeSwitcher.value.goToTable()
    }
</script>
